import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import usePlans from "app/hooks/usePlans";
import PricePlan from "../../extra-pages/PricingPlan/PricePlan";
import { useLocation, useNavigate } from "react-router-dom";
import Div from "@jumbo/shared/Div";
import FeaturesList from "./FeaturesList";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import { ROUTES } from "app/utils/constants/routeConstants";
import PaymentSuccessDialog from "components/PaymentSuccessDialog";
import { PaymentStatus } from "app/utils/constants/paymentStatus";
import LoadingView from "components/LoadingView";
import ErrorView from "components/ErrorView";
import useMetaTitle from "app/hooks/useMetaTitle";
import { ASSET_CODINGPAL } from "app/utils/constants/paths";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import GroupIcon from "@mui/icons-material/Group";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CodeIcon from "@mui/icons-material/Code";
import { lightBlue } from "@mui/material/colors";
import useLoginRedirect from "app/hooks/useLoginRedirect";
import EnrollButton from "components/EnrollButton";

const PricingPlan = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { authUser } = useJumboAuth();
  const { plans, loading, error } = usePlans();
  const [autoTriggerPlanId, setAutoTriggerPlanId] = useState(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentDialogStatus, setPaymentDialogStatus] = useState(null);
  const [paymentDialogPlanName, setPaymentDialogPlanName] = useState("");

  const isLoggedIn = !!authUser;

  const { handleProtectedAction, LoginDialog } = useLoginRedirect({
    isLoggedIn,
    dialogMessage: "Please login to reach out to us",
    confirmColor: "success",
  });

  useMetaTitle({
    title: "Upgrade",
  });

  // Check for paymentStatus in URL query parameters
  useEffect(() => {
    if (isLoggedIn) {
      const query = new URLSearchParams(location.search);
      const planId = query.get("planId");
      const paymentStatusFromUrl = query.get("paymentStatus");
      const planNameFromUrl = query.get("planName");

      if (planId) {
        setAutoTriggerPlanId(parseInt(planId, 10));
      }

      if (
        paymentStatusFromUrl &&
        planNameFromUrl &&
        paymentStatusFromUrl !== PaymentStatus.PAYMENT_COMPLETED
      ) {
        setPaymentDialogStatus(paymentStatusFromUrl);
        setPaymentDialogPlanName(planNameFromUrl);
        setShowPaymentDialog(true);
      }

      // Clear the query parameters to prevent re-showing on refresh
      navigate({ pathname: location.pathname }, { replace: true });
    }
  }, [isLoggedIn, location.search, navigate, location.pathname]);

  const handleDialogClose = () => {
    setShowPaymentDialog(false);
    setPaymentDialogStatus(null);
    setPaymentDialogPlanName("");
  };

  // onClose handler to prevent closing on backdrop click
  const handleDialogCloseOnlyOnContinue = (event, reason) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }
    handleDialogClose();
  };

  const redirectToUpgrade = () => {
    navigate(ROUTES.UPGRADE);
  };

  const handleViewDetails = () => {
    const queryParams = new URLSearchParams({
      size: "25",
      page: "1",
      sort: "id,asc",
      ProblemVideos: "true",
    });
    navigate({
      pathname: ROUTES.ALGOS_AND_DS,
      search: queryParams.toString(),
    });
  };

  if (loading) {
    return <LoadingView message="Loading plans..." />;
  }

  if (error) {
    return (
      <ErrorView
        error={error}
        onBack={() => navigate(-1)}
        backButtonLabel="Back"
      />
    );
  }

  return (
    <Div>
      <Div
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexDirection: "column",
        }}
      >
        <Typography
          variant="h1"
          sx={{
            textAlign: "center",
            fontSize: 32,
            fontWeight: 700,
            color: "text.primary",
          }}
        >
          Choose the Programming Plan that fits your needs
        </Typography>
      </Div>
      <Grid container spacing={3.75}>
        {/* Static Plan: Master DSA */}
        <Grid item xs={12} md={6} lg={4} xl={3}>
          <PricePlan
            title=""
            subheader=""
            headerSx={{
              textAlign: "center",
              backgroundImage: `url(${ASSET_CODINGPAL}/free-plan.png)`,
              backgroundSize: "contain",
              backgroundPosition: "center",
              height: { xs: 250, md: 200, lg: 172, xl: 260 },
            }}
            sx={{
              p: 0,
              height: "100%",
            }}
          >
            <Typography
              variant="h3"
              sx={{ color: "inherit", fontWeight: "500" }}
            >
              Master DSA
            </Typography>
            <List disablePadding sx={{ mb: 1 }}>
              <ListItem sx={{ px: 0 }}>
                <ListItemIcon
                  sx={{
                    minWidth: 32,
                    color: "text.secondary",
                  }}
                >
                  <LibraryBooksIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      variant="h5"
                      fontWeight="medium"
                      sx={{
                        margin: 0,
                      }}
                    >
                      400+ DSA Problems with explanations.
                    </Typography>
                  }
                />
              </ListItem>
              <ListItem sx={{ px: 0 }}>
                <ListItemIcon
                  sx={{
                    minWidth: 32,
                    color: "text.secondary",
                  }}
                >
                  <AccessTimeIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      variant="h5"
                      fontWeight="medium"
                      sx={{
                        margin: 0,
                      }}
                    >
                      Free videos to select problems.
                    </Typography>
                  }
                />
              </ListItem>
              <ListItem sx={{ px: 0 }}>
                <ListItemIcon
                  sx={{
                    minWidth: 32,
                    color: "text.secondary",
                  }}
                >
                  <GroupIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      variant="h5"
                      fontWeight="medium"
                      sx={{
                        margin: 0,
                      }}
                    >
                      All textual explanations along with Visualizations.
                    </Typography>
                  }
                />
              </ListItem>
              <ListItem sx={{ px: 0 }}>
                <ListItemIcon
                  sx={{
                    minWidth: 32,
                    color: "text.secondary",
                  }}
                >
                  <CalendarMonthIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      variant="h5"
                      fontWeight="medium"
                      sx={{
                        margin: 0,
                      }}
                    >
                      Lifetime free with no ads.
                    </Typography>
                  }
                />
              </ListItem>
              <ListItem sx={{ px: 0 }}>
                <ListItemIcon
                  sx={{
                    minWidth: 32,
                    color: "text.secondary",
                  }}
                >
                  <CodeIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      variant="h5"
                      fontWeight="medium"
                      sx={{
                        margin: 0,
                      }}
                    >
                      Code snippets of all approaches in JAVA, C++, Python, and
                      JavaScript
                    </Typography>
                  }
                />
              </ListItem>
            </List>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexWrap: "wrap",
                gap: 1,
                marginBottom: 2,
                width: "100%",
                textAlign: "center",
              }}
            >
              <Typography
                variant="h2"
                sx={{
                  fontWeight: "600",
                  fontSize: 32,
                  color: "#222d45",
                  m: 0,
                }}
              >
                Free
              </Typography>
            </Box>
            <Div
              sx={{
                display: "flex",
                gap: 2,
                flexDirection: "column",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Button
                variant="contained"
                sx={{ mb: 0.5 }}
                onClick={() => navigate(ROUTES.ALGOS_AND_DS)}
              >
                Explore
              </Button>
            </Div>
          </PricePlan>
        </Grid>

        {/* Dynamic Plans */}
        {plans.map((plan) => (
          <Grid item xs={12} md={6} lg={4} xl={3} key={plan.id}>
            <PricePlan
              title=""
              subheader=""
              headerSx={{
                textAlign: "center",
                bgcolor:
                  plan.variant === "personal" || plan.variant === "professional"
                    ? "primary.main"
                    : "primary.dark",
                ...(plan.thumbnail && plan.thumbnailContentType
                  ? {
                      backgroundImage: `url(data:${plan.thumbnailContentType};base64,${plan.thumbnail})`,
                      backgroundSize: "contain",
                      backgroundPosition: "center",
                      height: { xs: 220, md: 200, lg: 172, xl: 260 },
                    }
                  : {
                      height: { xs: 250, md: 200, lg: 172, xl: 260 },
                    }),
              }}
              sx={{
                p: 0,
                height: "100%",
              }}
            >
              <Typography
                variant="h3"
                sx={{ color: "inherit", fontWeight: "500" }}
              >
                {plan.name}
              </Typography>
              <FeaturesList plan={plan} isPopular={plan.isPopular} />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexWrap: "wrap",
                  gap: 1,
                  marginBottom: 2,
                  width: "100%",
                  textAlign: "center",
                }}
              >
                <Typography
                  component="s"
                  variant="body1"
                  sx={{
                    color: "text.secondary",
                    fontSize: "1rem",
                    fontWeight: 400,
                    marginRight: 1,
                  }}
                >
                  ₹ 8000
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: "600",
                    fontSize: 32,
                    color: "#222d45",
                    m: 0,
                  }}
                >
                  ₹ {plan.price.toFixed(0) / 100}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: "1rem",
                    fontWeight: 500,
                  }}
                >
                  /{" "}
                  {`${plan.validity} ${plan.validityUnit
                    .slice(0, 1)
                    .toUpperCase()}${plan.validityUnit.slice(1).toLowerCase()}`}
                </Typography>
              </Box>
              <Div
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                {/* <Button
                  onClick={() =>
                    handleProtectedAction(ROUTES.REPORT, {
                      queryParams: { planId: plan.id },
                    })
                  }
                  sx={{
                    textTransform: "none",
                    fontSize: 16,
                    padding: 1,
                    "&:hover": {
                      color: lightBlue[600],
                      backgroundColor: lightBlue[50],
                      borderRadius: 2,
                      padding: 1,
                    },
                  }}
                >
                  Reach out to us
                </Button> */}
                <EnrollButton
                  plan={plan}
                  isLoggedIn={isLoggedIn}
                  authUser={authUser}
                  navigate={navigate}
                  location={location}
                  width="60%"
                  autoTrigger={!!autoTriggerPlanId}
                  autoTriggerPlanId={autoTriggerPlanId}
                />
                <Button
                  onClick={handleViewDetails}
                  sx={{
                    textTransform: "none",
                    fontSize: 16,
                  }}
                >
                  View complete details
                </Button>
              </Div>
            </PricePlan>
          </Grid>
        ))}
      </Grid>
      <PaymentSuccessDialog
        open={showPaymentDialog}
        onClose={handleDialogCloseOnlyOnContinue}
        paymentStatus={paymentDialogStatus}
        planName={paymentDialogPlanName}
        redirectToUpgrade={redirectToUpgrade}
        disableEscapeKeyDown={true}
      />
      {LoginDialog}
    </Div>
  );
};

export default PricingPlan;
